import { useState } from 'react'
import {
  getPresignedUploadUrl,
  uploadFileToPresignedUrl,
} from 'utils/api/uploadApi'

interface UsePresignedUploadOptions {
  module: string
  onProcess: (key: string) => Promise<unknown>
}

interface UsePresignedUploadResult {
  uploadProgress: number | undefined
  isUploading: boolean
  uploadAndProcess: (file: File) => Promise<unknown>
}

/**
 * Generic presigned-S3-upload flow, reused across every feature that needs
 * one: request a presigned URL for `module`, PUT the file straight to S3
 * (tracking progress), then call `onProcess(key)` to trigger whatever the
 * backend does with the uploaded file. Mirrors the sequence originally
 * written inline for Clone Inventory/Pricing (getUploadUrlToCloneInventoryPricing
 * + uploadFileToPresignedUrl + a follow-up API call) - extracted here so new
 * features (e.g. Bundle Product import) don't duplicate it.
 */
const usePresignedUpload = ({
  module,
  onProcess,
}: UsePresignedUploadOptions): UsePresignedUploadResult => {
  const [uploadProgress, setUploadProgress] = useState<number | undefined>(
    undefined,
  )
  const [isUploading, setIsUploading] = useState(false)

  const uploadAndProcess = async (file: File) => {
    setIsUploading(true)
    setUploadProgress(0)

    try {
      const { key, url } = await getPresignedUploadUrl({
        module,
        fileName: file.name,
        fileType: file.type,
      })

      await uploadFileToPresignedUrl({
        file,
        uploadUrl: url,
        onProgress: (percent) => setUploadProgress(percent),
      })

      return await onProcess(key)
    } finally {
      setIsUploading(false)
      setUploadProgress(undefined)
    }
  }

  return { uploadProgress, isUploading, uploadAndProcess }
}

export default usePresignedUpload
